<template>
  <div class="app-container bg-gray-100">
    <header class="bg-blue-600 text-white p-4 shadow-md">
      <div class="container mx-auto flex items-center">
        <img 
          src="/ie11-logo.png" 
          alt="Internet Explorer Logo" 
          class="w-10 h-10 mr-3  p-1"
        />
        <div>
          <h1 class="text-2xl font-bold">IMQA IE11 Tester</h1>
          <p class="text-sm">Test IMQA Web Agent compatibility with IE11</p>
        </div>
      </div>
    </header>
    
    <main class="container mx-auto p-4">
      <!-- Configuration Panel -->
      <section class="card mb-4">
        <div class="card-header bg-gray-200">
          <h2 class="text-lg font-semibold">IMQA Configuration</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Collector URL</label>
              <input type="text" class="form-control" v-model="imqaConfig.collectorUrl">
            </div>
            <div class="form-group">
              <label class="form-label">Service Name</label>
              <input type="text" class="form-control" v-model="imqaConfig.serviceName">
            </div>
            <div class="form-group">
              <label class="form-label">Service Version</label>
              <input type="text" class="form-control" v-model="imqaConfig.serviceVersion">
            </div>
            <div class="form-group">
              <label class="form-label">Service Key</label>
              <input type="text" class="form-control" v-model="imqaConfig.serviceKey">
            </div>
          </div>
          
          <div class="mt-4 flex flex-wrap gap-2">
            <label class="inline-flex items-center mr-4">
              <input type="checkbox" class="form-checkbox" v-model="imqaConfig.debug">
              <span class="ml-2">Debug Mode</span>
            </label>
            <label class="inline-flex items-center mr-4">
              <input type="checkbox" class="form-checkbox" v-model="imqaConfig.webVitals">
              <span class="ml-2">Web Vitals</span>
            </label>
            <label class="inline-flex items-center mr-4">
              <input type="checkbox" class="form-checkbox" v-model="imqaConfig.consoleCapture">
              <span class="ml-2">Console Capture</span>
            </label>
            <label class="inline-flex items-center mr-4">
              <input type="checkbox" class="form-checkbox" v-model="imqaConfig.advancedNetworkCapture">
              <span class="ml-2">Advanced Network Capture</span>
            </label>
          </div>
          
          <div class="mt-4">
            <button @click="initializeIMQA" class="btn btn-primary mr-2" :disabled="imqaInitialized">
              Initialize IMQA
            </button>
            <button @click="deinitializeIMQA" class="btn btn-secondary mr-2" :disabled="!imqaInitialized">
              Deinitialize
            </button>
            <button @click="resetConfig" class="btn btn-danger">
              Reset Config
            </button>
          </div>
          
          <div v-if="imqaInitialized" class="mt-4 p-2 bg-green-100 text-green-800 rounded">
            <strong>Status:</strong> IMQA Initialized
            <div v-if="sessionId">
              <strong>Session ID:</strong> {{ sessionId }}
            </div>
          </div>
          
          <div v-if="initError" class="mt-4 p-2 bg-red-100 text-red-800 rounded">
            <strong>Error:</strong> {{ initError }}
          </div>
        </div>
      </section>
      
      <!-- Test Actions Panel -->
      <section class="card mb-4" :class="{ 'opacity-50 pointer-events-none': !imqaInitialized }">
        <div class="card-header bg-gray-200">
          <h2 class="text-lg font-semibold">Test IMQA API</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- API Test Buttons -->
            <div>
              <h3 class="text-md font-semibold mb-2">Core API</h3>
              <div class="flex flex-wrap gap-2 ie11-gap">
                <button @click="testAddAction" class="btn btn-primary mb-2">
                  Test addAction
                </button>
                <button @click="testRecordException" class="btn btn-danger mb-2">
                  Test recordException
                </button>
                <button @click="testRecordLog" class="btn btn-secondary mb-2">
                  Test recordLog
                </button>
                <button @click="testSetGlobalAttributes" class="btn btn-secondary mb-2">
                  Test setGlobalAttributes
                </button>
                <button @click="testSetUserIdAttribute" class="btn btn-secondary mb-2">
                  Test setUserIdAttribute
                </button>
              </div>
            </div>
            
            <!-- Network Test Buttons -->
            <div>
              <h3 class="text-md font-semibold mb-2">Network Requests</h3>
              <div class="flex flex-wrap gap-2 ie11-gap">
                <button @click="testXHR" class="btn btn-primary mb-2">
                  Test XMLHttpRequest
                </button>
                <button @click="testFetch" class="btn btn-primary mb-2">
                  Test Fetch API
                </button>
                <button @click="testNetworkError" class="btn btn-danger mb-2">
                  Test Network Error
                </button>
                <button @click="toggleAdvancedNetworkCapture" class="btn btn-secondary mb-2">
                  {{ imqaConfig.advancedNetworkCapture ? 'Disable' : 'Enable' }} Network Capture
                </button>
              </div>
            </div>
          </div>
          
          <!-- Console & Error Tests -->
          <div class="mt-4">
            <h3 class="text-md font-semibold mb-2">Console & Errors</h3>
            <div class="flex flex-wrap gap-2 ie11-gap">
              <button @click="testConsoleLog" class="btn btn-secondary mb-2">
                Test Console.log
              </button>
              <button @click="testConsoleError" class="btn btn-danger mb-2">
                Test Console.error
              </button>
              <button @click="testUncaughtError" class="btn btn-danger mb-2">
                Test Uncaught Error
              </button>
              <button @click="testPromiseRejection" class="btn btn-danger mb-2">
                Test Promise Rejection
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Results Panel -->
      <section class="card">
        <div class="card-header bg-gray-200">
          <h2 class="text-lg font-semibold">Test Results</h2>
        </div>
        <div class="card-body">
          <div v-if="testResults.length === 0" class="text-gray-500 text-center py-4">
            No test results yet. Run some tests to see results here.
          </div>
          <div v-else class="max-h-96 overflow-y-auto">
            <div 
              v-for="result in testResults" 
              :key="result.id" 
              class="mb-2 p-2 rounded text-sm"
              :class="{
                'bg-green-100 text-green-800': result.type === 'success',
                'bg-red-100 text-red-800': result.type === 'error',
                'bg-blue-100 text-blue-800': result.type === 'info'
              }"
            >
              <div class="flex justify-between">
                <strong>{{ result.message }}</strong>
                <span class="text-xs text-gray-600">{{ formatTime(result.timestamp) }}</span>
              </div>
              <div v-if="result.data" class="mt-1 text-xs">
                <pre class="whitespace-pre-wrap">{{ typeof result.data === 'object' ? JSON.stringify(result.data, null, 2) : result.data }}</pre>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <footer class="bg-gray-800 text-white p-4 mt-4">
      <div class="container mx-auto text-center">
        <p>IMQA &copy; 2025</p>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      userId: 'user_' + Math.floor(Math.random() * 1000)
    };
  },
  computed: {
    imqaConfig: {
      get() {
        return this.$store.state.imqaConfig;
      },
      set(value) {
        this.$store.commit('updateImqaConfig', value);
      }
    },
    imqaInitialized() {
      return this.$store.state.imqaInitialized;
    },
    testResults() {
      return this.$store.state.testResults;
    },
    sessionId() {
      return this.$store.state.sessionId;
    },
    initError() {
      return this.$store.state.initError;
    }
  },
  methods: {
    initializeIMQA() {
      this.$store.dispatch('initializeIMQA');
    },
    deinitializeIMQA() {
      this.$store.dispatch('deinitializeIMQA');
    },
    resetConfig() {
      this.$store.commit('resetImqaConfig');
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    },
    testAddAction() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'addAction',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          window.IMQA.addAction('Test Action', {
            actionType: 'click',
            elementId: 'test-button',
            timestamp: Date.now()
          });
          return 'Action added';
        }
      });
    },
    testRecordException() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'recordException',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          const error = new Error('Test exception');
          window.IMQA.recordException(error, {
            severity: 'medium',
            location: 'test function'
          });
          return 'Exception recorded';
        }
      });
    },
    testRecordLog() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'recordLog',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          window.IMQA.recordLog('This is a test log message', {
            logLevel: 'info',
            component: 'TestComponent'
          });
          return 'Log recorded';
        }
      });
    },
    testSetGlobalAttributes() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'setGlobalAttributes',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          window.IMQA.setGlobalAttributes({
            environment: 'testing',
            browser: 'IE11',
            testRun: 'manual-test-' + Date.now()
          });
          return 'Global attributes set';
        }
      });
    },
    testSetUserIdAttribute() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'setUserIdAttribute',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          window.IMQA.setUserIdAttribute(this.userId);
          return `User ID set to ${this.userId}`;
        }
      });
    },
    testXHR() {
      this.$store.dispatch('runTest', {
        type: 'network',
        name: 'XMLHttpRequest',
        fn: () => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', 'https://jsonplaceholder.typicode.com/todos/1');
          xhr.onload = () => {
            if (xhr.status === 200) {
              this.$store.commit('addTestResult', {
                type: 'success',
                message: 'XHR request completed',
                data: JSON.parse(xhr.responseText)
              });
            } else {
              this.$store.commit('addTestResult', {
                type: 'error',
                message: `XHR request failed with status ${xhr.status}`
              });
            }
          };
          xhr.onerror = () => {
            this.$store.commit('addTestResult', {
              type: 'error',
              message: 'XHR request failed'
            });
          };
          xhr.send();
          return 'XHR request sent';
        }
      });
    },
    testFetch() {
      this.$store.dispatch('runTest', {
        type: 'network',
        name: 'Fetch API',
        fn: () => {
          fetch('https://jsonplaceholder.typicode.com/todos/1')
            .then(response => response.json())
            .then(data => {
              this.$store.commit('addTestResult', {
                type: 'success',
                message: 'Fetch request completed',
                data
              });
            })
            .catch(error => {
              this.$store.commit('addTestResult', {
                type: 'error',
                message: 'Fetch request failed',
                data: error.toString()
              });
            });
          return 'Fetch request sent';
        }
      });
    },
    testNetworkError() {
      this.$store.dispatch('runTest', {
        type: 'network',
        name: 'Network Error',
        fn: () => {
          fetch('https://invalid-domain-123456789.com')
            .then(response => response.json())
            .catch(error => {
              this.$store.commit('addTestResult', {
                type: 'success',
                message: 'Network error caught successfully',
                data: error.toString()
              });
            });
          return 'Network error test initiated';
        }
      });
    },
    toggleAdvancedNetworkCapture() {
      this.$store.dispatch('runTest', {
        type: 'api',
        name: 'Toggle Advanced Network Capture',
        fn: () => {
          if (!window.IMQA) throw new Error('IMQA not initialized');
          
          const newValue = !this.imqaConfig.advancedNetworkCapture;
          
          if (newValue) {
            window.IMQA.enableAdvancedNetworkCapture();
          } else {
            window.IMQA.disableAdvancedNetworkCapture();
          }
          
          this.$store.commit('updateImqaConfig', { advancedNetworkCapture: newValue });
          
          return `Advanced Network Capture ${newValue ? 'enabled' : 'disabled'}`;
        }
      });
    },
    testConsoleLog() {
      this.$store.dispatch('runTest', {
        type: 'console',
        name: 'Console.log',
        fn: () => {
          console.log('This is a test console log', { 
            timestamp: Date.now(),
            testId: 'console-log-test'
          });
          return 'Console.log executed';
        }
      });
    },
    testConsoleError() {
      this.$store.dispatch('runTest', {
        type: 'console',
        name: 'Console.error',
        fn: () => {
          console.error('This is a test console error', { 
            timestamp: Date.now(),
            testId: 'console-error-test'
          });
          return 'Console.error executed';
        }
      });
    },
    testUncaughtError() {
      this.$store.dispatch('runTest', {
        type: 'error',
        name: 'Uncaught Error',
        fn: () => {
          setTimeout(() => {
            throw new Error('This is a test uncaught error');
          }, 0);
          return 'Uncaught error test initiated';
        }
      });
    },
    testPromiseRejection() {
      this.$store.dispatch('runTest', {
        type: 'error',
        name: 'Promise Rejection',
        fn: () => {
          new Promise((resolve, reject) => {
            reject(new Error('This is a test promise rejection'));
          }).catch(err => {
            console.log('Promise rejection caught locally');
          });
          return 'Promise rejection test initiated';
        }
      });
    }
  }
};
</script>

<style>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

main {
  flex: 1;
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .grid {
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    margin: -0.5rem;
  }
  
  .grid > * {
    -ms-flex: 0 0 100%;
    flex: 0 0 100%;
    padding: 0.5rem;
  }
  
  @media (min-width: 768px) {
    .md\:grid-cols-2 > * {
      -ms-flex: 0 0 50%;
      flex: 0 0 50%;
    }
  }
  
  .container {
    width: 100%;
    padding-right: 1rem;
    padding-left: 1rem;
    margin-right: auto;
    margin-left: auto;
  }
  
  @media (min-width: 640px) {
    .container {
      max-width: 640px;
    }
  }
  
  @media (min-width: 768px) {
    .container {
      max-width: 768px;
    }
  }
  
  @media (min-width: 1024px) {
    .container {
      max-width: 1024px;
    }
  }
  
  @media (min-width: 1280px) {
    .container {
      max-width: 1280px;
    }
  }
  .ie11-gap > * {
    margin-right: 0.5rem; /* Equivalent to gap-2 */
  }
}
</style>